import React from 'react';
import { ScrollText } from 'lucide-react';
import { LegalPage } from './LegalPage';

interface Props {
  onBack: () => void;
  onNavigatePrivacy: () => void;
  onNavigateTerms: () => void;
}

export const TermsConditions: React.FC<Props> = ({ onBack, onNavigatePrivacy, onNavigateTerms }) => {
  return (
      <LegalPage 
        title="Terms and Conditions" 
        lastUpdated="January 12, 2026" /* @live-data-exempt: legal-effective-date */ 
        icon={<ScrollText size={32} strokeWidth={1.5} />}
        onBack={onBack}
        onNavigatePrivacy={onNavigatePrivacy}
      onNavigateTerms={onNavigateTerms}
    >
      <p>Welcome to <strong>Intellio</strong>. These Terms and Conditions ("Terms") govern your access to and use of the Intellio platform and related services (collectively, the "Service") provided by Intellio Labs, Inc. ("<strong>Intellio</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>").</p>
      <br /><br />
      <p>By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.</p>
      <br /><br />

      <h2>1. ACCEPTANCE OF TERMS</h2>
      <p>By creating an account, accessing, or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our <strong>Privacy Policy</strong>, which is incorporated herein by reference. If you are using the Service on behalf of an educational institution or organization, you represent and warrant that you have the authority to bind that entity to these Terms.</p>
      <br /><br />

      <h2>2. DESCRIPTION OF SERVICE</h2>
      <p>Intellio provides an <strong>AI-powered automation platform</strong> designed to streamline administrative operations for K-12 educational institutions. Our Service includes features for admissions management, enrollment processing, financial aid administration, student information management, parent communications, and related educational administrative functions.</p>
      <br /><br />
      <p>The Service is designed to integrate with and support existing school operations. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.</p>
      <br /><br />

      <h2>3. ELIGIBILITY AND ACCOUNT REGISTRATION</h2>
      <h3>3.1 Eligibility</h3>
      <p>The Service is intended for use by educational institutions, school administrators, teachers, parents, and authorized personnel associated with educational institutions. Users must be at least <strong>18 years of age</strong> to create an account. For users under 18, an authorized parent, guardian, or school administrator must provide consent and supervision.</p>
      <br /><br />
      <h3>3.2 Account Registration</h3>
      <p>To access certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.</p>
      <br /><br />
      <h3>3.3 Account Security</h3>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.</p>
      <br /><br />

      <h2>4. ACCEPTABLE USE POLICY</h2>
      <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
      <ul>
        <li>Use the Service in any way that violates any applicable federal, state, local, or international law or regulation</li>
        <li>Transmit any material that is defamatory, obscene, offensive, or otherwise objectionable</li>
        <li>Impersonate or attempt to impersonate Intellio, an Intellio employee, another user, or any other person or entity</li>
        <li>Engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Service</li>
        <li>Use any automated system, including "robots," "spiders," or "offline readers," to access the Service in a manner that sends more request messages to our servers than a human could reasonably produce in the same period</li>
        <li>Attempt to gain unauthorized access to any portion of the Service or any other systems or networks connected to the Service</li>
        <li>Use the Service to transmit any viruses, malware, or other malicious code</li>
        <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
        <li>Remove, alter, or obscure any proprietary notices on the Service</li>
      </ul>
      <br /><br />

      <h2>5. PRIVACY AND DATA PROTECTION</h2>
      <h3>5.1 Student Data Protection</h3>
      <p>Intellio is committed to protecting the privacy and security of student data in compliance with applicable laws, including the <strong>Family Educational Rights and Privacy Act (FERPA)</strong>, the <strong>Children's Online Privacy Protection Act (COPPA)</strong>, and applicable state privacy laws.</p>
      <br /><br />
      <h3>5.2 Data Use</h3>
      <p>We will use student and user data only as necessary to provide the Service and as described in our Privacy Policy. We will <strong>not sell, rent, or share</strong> personal information with third parties for their marketing purposes.</p>
      <br /><br />
      <h3>5.3 Educational Institution Responsibilities</h3>
      <p>Educational institutions are responsible for obtaining any necessary consents from parents or guardians for the collection and use of student information through the Service.</p>
      <br /><br />
      <h3>5.4 Data Security</h3>
      <p>We employ industry-standard security measures to protect data transmitted through and stored on the Service. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
      <br /><br />

      <h2>6. INTELLECTUAL PROPERTY RIGHTS</h2>
      <h3>6.1 Intellio Property</h3>
      <p>The Service and its entire contents, features, and functionality (including but not limited to all information, software, code, text, displays, graphics, photographs, video, audio, design, presentation, selection, and arrangement) are owned by <strong>Intellio Labs, Inc.</strong>, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
      <br /><br />
      <h3>6.2 Limited License</h3>
      <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service for your educational institution's internal purposes.</p>
      <br /><br />
      <h3>6.3 Trademarks</h3>
      <p>The <strong>Intellio</strong> name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Intellio Labs, Inc. You may not use such marks without our prior written permission.</p>
      <br /><br />
      <h3>6.4 User Content</h3>
      <p>By submitting, posting, or uploading content to the Service, you grant Intellio a worldwide, non-exclusive, royalty-free license to use, copy, reproduce, process, adapt, modify, publish, transmit, display, and distribute such content solely for the purpose of providing and improving the Service.</p>
      <br /><br />

      <h2>7. PAYMENT TERMS</h2>
      <h3>7.1 Subscription Fees</h3>
      <p>Access to certain features of the Service may require payment of subscription fees. All fees are due in accordance with the payment terms presented to you when you subscribe.</p>
      <br /><br />
      <h3>7.2 Billing</h3>
      <p>By providing a payment method, you authorize us to charge all fees incurred through your account to that payment method. You are responsible for providing accurate and current billing information.</p>
      <br /><br />
      <h3>7.3 Renewal and Cancellation</h3>
      <p>Subscriptions will automatically renew at the end of each billing period unless you cancel before the renewal date. You may cancel your subscription at any time through your account settings or by contacting us.</p>
      <br /><br />
      <h3>7.4 Refunds</h3>
      <p>Fees paid are <strong>non-refundable</strong> except as required by law or as expressly stated in these Terms.</p>
      <br /><br />

      <h2>8. ARTIFICIAL INTELLIGENCE AND AUTOMATED PROCESSING</h2>
      <h3>8.1 AI Features</h3>
      <p>The Service incorporates <strong>artificial intelligence and machine learning technologies</strong> to automate administrative tasks and provide intelligent assistance. While we strive for accuracy, AI-generated outputs may contain errors or inaccuracies.</p>
      <br /><br />
      <h3>8.2 Human Oversight</h3>
      <p>Educational institutions are responsible for implementing appropriate <strong>human oversight</strong> and review of AI-generated outputs, particularly for decisions affecting students, admissions, or financial aid.</p>
      <br /><br />
      <h3>8.3 No Guarantee of Outcomes</h3>
      <p>We do not guarantee that the use of our AI features will result in any particular outcome or improvement in operational efficiency.</p>
      <br /><br />

      <h2>9. THIRD-PARTY SERVICES AND INTEGRATIONS</h2>
      <p>The Service may contain links to third-party websites or services that are not owned or controlled by Intellio. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by your use of any such third-party services.</p>
      <br /><br />

      <h2>10. DISCLAIMER OF WARRANTIES</h2>
      <p className="uppercase font-bold">THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.</p>
      <br /><br />
      <p className="uppercase font-bold">INTELLIO DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS. INTELLIO DOES NOT WARRANT OR MAKE ANY REPRESENTATIONS REGARDING THE USE OR THE RESULTS OF THE USE OF THE SERVICE IN TERMS OF ACCURACY, RELIABILITY, OR OTHERWISE.</p>
      <br /><br />

      <h2>11. LIMITATION OF LIABILITY</h2>
      <p className="uppercase font-bold">TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL INTELLIO LABS, INC., ITS AFFILIATES, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:</p>
      <ul>
        <li>YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE</li>
        <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE</li>
        <li>ANY CONTENT OBTAINED FROM THE SERVICE</li>
        <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT</li>
      </ul>
      <br /><br />
      <p className="uppercase font-bold">IN NO EVENT SHALL INTELLIO'S TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES, OR CAUSES OF ACTION EXCEED THE AMOUNT YOU HAVE PAID TO INTELLIO IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.</p>
      <br /><br />

      <h2>12. INDEMNIFICATION</h2>
      <p>You agree to defend, indemnify, and hold harmless <strong>Intellio Labs, Inc.</strong>, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:</p>
      <ul>
        <li>Your violation of these Terms</li>
        <li>Your use of the Service</li>
        <li>Your violation of any rights of another</li>
        <li>Your violation of any applicable laws or regulations</li>
      </ul>
      <br /><br />

      <h2>13. TERMINATION</h2>
      <h3>13.1 Termination by You</h3>
      <p>You may terminate your account at any time by following the account closure process in the Service or by contacting us.</p>
      <br /><br />
      <h3>13.2 Termination by Intellio</h3>
      <p>We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including but not limited to breach of these Terms. Upon termination, your right to use the Service will immediately cease.</p>
      <br /><br />
      <h3>13.3 Effect of Termination</h3>
      <p>Upon termination, we will make student data available for export for a period of thirty (30) days, after which we may delete such data in accordance with our data retention policies and applicable law.</p>
      <br /><br />

      <h2>14. CHANGES TO TERMS</h2>
      <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Service and updating the "Last Updated" date. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.</p>
      <br /><br />

      <h2>15. GOVERNING LAW AND DISPUTE RESOLUTION</h2>
      <h3>15.1 Governing Law</h3>
      <p>These Terms shall be governed by and construed in accordance with the laws of the <strong>State of New York</strong>, without regard to its conflict of law provisions.</p>
      <br /><br />
      <h3>15.2 Dispute Resolution</h3>
      <p>Any dispute arising from these Terms or the Service shall be resolved through <strong>binding arbitration</strong> in accordance with the American Arbitration Association's Commercial Arbitration Rules, except that either party may seek injunctive or other equitable relief in any court of competent jurisdiction.</p>
      <br /><br />
      <h3>15.3 Class Action Waiver</h3>
      <p>You agree that any arbitration or proceeding shall be limited to the dispute between you and Intellio individually. You agree to waive any right to have any dispute brought, heard, or arbitrated as a class action, collective action, or representative action.</p>
      <br /><br />

      <h2>16. SEVERABILITY</h2>
      <p>If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect. The invalid or unenforceable provision will be deemed modified to the extent necessary to make it valid and enforceable.</p>
      <br /><br />

      <h2>17. WAIVER</h2>
      <p>No waiver by Intellio of any term or condition set forth in these Terms shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition.</p>
      <br /><br />

      <h2>18. ENTIRE AGREEMENT</h2>
      <p>These Terms, together with our <strong>Privacy Policy</strong> and any other legal notices or agreements published by us on the Service, constitute the entire agreement between you and Intellio Labs, Inc. regarding the Service and supersede all prior agreements and understandings.</p>
      <br /><br />

      <h2>19. CONTACT INFORMATION</h2>
      <p>If you have any questions about these Terms, please contact us:</p>
      <p>
        <strong>Intellio Labs, Inc.</strong><br />
        Email: <a href="mailto:legal@intellio.com">legal@intellio.com</a><br />
        Address: [Your Business Address]<br />
        Phone: [Your Phone Number]
      </p>
      <br /><br />

      <h2>20. ACKNOWLEDGMENT</h2>
      <p className="font-bold uppercase">
        BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS AND CONDITIONS, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM.
      </p>
    </LegalPage>
  );
};
